import { Box, Drawer } from '@mui/material'
import { useContext } from 'react'
import {
  allAvailableEventTypes,
  SESSION_PLATFORM_HOME_VISIT,
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_ONLINE,
} from 'src/consumer/constants'
import { normalizeScreeningSpecialtyForPayload } from 'src/consumer/helpers/screeningSpecialtyUtils'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { checkAndGetTargetValue } from 'src/consumer/helpers/utilFunctions'
import { GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { getMPDataWithoutParams } from 'src/mentor/services'
import BaseAddCustomPriceComponent from '../BaseAddCustomPriceComponent'
import ClinicAssociationSelector from './formSections/ClinicAssociationSelector'
import DurationAndBuffers from './formSections/DurationAndBuffers'
import EventBasicFields from './formSections/EventBasicFields'
import EventFormHeader from './formSections/EventFormHeader'
import EventTypeConfiguration from './formSections/EventTypeConfiguration'
import FormActionButtons from './formSections/FormActionButtons'
import LocationConfiguration from './formSections/LocationConfiguration'
import PanelAndWebinarConfig from './formSections/PanelAndWebinarConfig'
import PricingConfiguration from './formSections/PricingConfiguration'
import SessionOptionsCheckboxes from './formSections/SessionOptionsCheckboxes'
import useEventFormState from './hooks/useEventFormState'

export default function EventTypeCreationForm({
  eventFormModalData,
  setEventFormModalOpen,
  displayNamesOfEventTypes,
  handleEventCreation,
  oneOnOnePlans,
  bundlePacks,
  refreshBundlePacks,
  refreshOneOnOnePlans,
  saving,
  mentorProfileData,
  mentorClinicAssociationData,
  forceShowTherapistEventTypes = false,
  saveOneOnOnePlanApi,
  forceShowDoctorEventTypes = false,
  showNextVisitSlotSelectorEnabler = false,
  isDisabled = false,
  layout = 'modal',
  // Phase 14az — endpoint backing the Panel Members picker. Defaults to the mentor-
  // portal teacher-scoped route; the support portal overrides this with
  // /api/secure/cc/support/therapists/panelists/list which accepts a clinicLocationId
  // query param to scope panelists to people attached to that clinic. The clinic
  // location id rides in via `basicEventObject.contextId` when the event is clinic-
  // attached; we forward it on every request and the backend ignores it on routes
  // that don't read the param (the mentor route stays platform-wide).
  panelistsApiPath = '/api/secure/teacher/other-interviewers/list',
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)

  const {
    basicEventObject,
    setBasicEventObject,
    selectedPanelMembers,
    setSelectedPanelMembers,
    isCustomTimeChosen,
    setIsCustomTimeChosen,
    viewPriceCountry,
    setViewPriceCountry,
    showAddCustomPrice,
    setShowAddCustomPrice,
    oneOnOnePlanToEdit,
    setOneOnOnePlanToEdit,
    changeLocationBasedOnMentorOrClinicLocation,
  } = useEventFormState({
    eventFormModalData,
    oneOnOnePlans,
    mentorClinicAssociationData,
    mentorProfileData,
  })

  function saveEvent() {
    let eventObjectToSend = { ...basicEventObject }
    const validMeetingSrcValues = [
      SESSION_PLATFORM_ONLINE,
      SESSION_PLATFORM_OFFLINE,
      SESSION_PLATFORM_HOME_VISIT,
    ]
    if (!validMeetingSrcValues.includes(eventObjectToSend?.meetingSrc)) {
      eventObjectToSend.meetingSrc = SESSION_PLATFORM_ONLINE
    }
    if (eventObjectToSend?.type === allAvailableEventTypes?.assessment) {
      const ids = (eventObjectToSend?.psychAssessmentIds || [])
        .map((entry) => entry?.id ?? entry?.value)
        .filter(Boolean)
      eventObjectToSend.psychAssessmentIds = ids
      eventObjectToSend.clinicalAssessmentIds = ids
    }
    // Phase 14bb — normalise targetSpecialtySlug for ANY event that has it set, not
    // just screening. The picker is now exposed for every clinical event type
    // (oneOnOne / screening / therapySession / assessment / consultation), so the
    // legacy "Default → drop the field" stripping has to apply uniformly.
    // normalizeScreeningSpecialtyForPayload returns undefined when the value is empty
    // or the magic 'psychiatry' default sentinel — keeping the payload free of noise
    // and the EventType.targetSpecialtySlug field unset on the matching events.
    if (
      eventObjectToSend?.targetSpecialtySlug != null ||
      eventObjectToSend?.screeningType != null
    ) {
      const slug = normalizeScreeningSpecialtyForPayload(
        eventObjectToSend.targetSpecialtySlug ?? eventObjectToSend.screeningType,
      )
      if (slug) {
        eventObjectToSend.targetSpecialtySlug = slug
      } else {
        delete eventObjectToSend.targetSpecialtySlug
        delete eventObjectToSend.screeningType
      }
    }
    handleEventCreation(eventObjectToSend)
  }

  const handleValidation = (data) => {
    if (!data?.title) {
      return false
    }
    if (data?.type !== 'webinar') {
      if (!(parseInt(data?.durationInMins, 10) > 2)) {
        return false
      }
    } else {
      const maxUsers = parseInt(data?.maxUsers, 10)
      const minUsers = parseInt(data?.minUsers, 10)
      if (maxUsers < minUsers) {
        return false
      }
      if (maxUsers < 1) {
        return false
      }
      if (minUsers < 0) {
        return false
      }
    }
    if (!data?.free) {
      const planId =
        data?.type === allAvailableEventTypes.webinar
          ? data?.pricingPlanId || data?.subscriptionPlanId
          : data?.subscriptionPlanId
      if (!planId) {
        return false
      }
    }
    return true
  }

  function handleChangeLocation(event) {
    const { id } = event.target
    setBasicEventObject((prev) => ({
      ...prev,
      location: { ...(prev?.location || {}), [id]: checkAndGetTargetValue(event) },
    }))
  }

  function handleEditOneOnOnePlan() {
    const foundPlan = oneOnOnePlans?.find(
      (item) => item?.id === basicEventObject?.subscriptionPlanId,
    )
    setOneOnOnePlanToEdit(foundPlan)
    setShowAddCustomPrice(true)
  }

  function changeCountry(selectValue) {
    setBasicEventObject((prev) => ({
      ...prev,
      location: { ...prev?.location, countryCode: selectValue?.id, country: selectValue?.name },
    }))
  }

  function changePlan(e) {
    if (e.target.value !== 'create-plan') {
      basicEventObject?.type !== allAvailableEventTypes?.webinar
        ? setBasicEventObject((prev) => ({ ...prev, subscriptionPlanId: e.target.value }))
        : setBasicEventObject((prev) => ({ ...prev, pricingPlanId: e.target.value }))
    } else {
      setOneOnOnePlanToEdit()
      setShowAddCustomPrice(true)
    }
  }

  function handleCloseAddCustomPrice() {
    setShowAddCustomPrice(false)
  }

  function setBundle(value) {
    setBasicEventObject((prev) => {
      if (typeof value === 'function') {
        return { ...prev, bundlePlanIds: value(prev?.bundlePlanIds || []) }
      } else {
        return { ...prev, bundlePlanIds: { ...(prev.bundlePlanIds || []), ...value } }
      }
    })
  }

  async function loadOptions(search, loadedOptions, { page }) {
    // Phase 14az — clinic-scoped panel members. When the event being edited is attached
    // to a clinic location (basicEventObject.contextId is the clinic location id, set by
    // the support portal when an event is associated with a clinic), forward
    // `clinicLocationId` so the support endpoint filters to practitioners actually
    // working at that clinic. Mentor portal callers don't pass this — they hit the
    // teacher-scoped route which ignores the param and returns the platform-wide
    // paginated list as before.
    const clinicLocationId = basicEventObject?.contextId || basicEventObject?.clinicLocationId || ''
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (page != null) params.set('page', String(page))
    if (clinicLocationId) params.set('clinicLocationId', clinicLocationId)
    const queryString = params.toString()
    const url = queryString ? `${panelistsApiPath}?${queryString}` : panelistsApiPath
    const response = await getMPDataWithoutParams(url)
    var hasMore = true
    var optionsArray
    if (response?.status === 200) {
      // The support endpoint returns the array directly; the mentor endpoint also
      // returns the array directly (Callable<List<Practitioner>>). Both shapes land
      // on response.data uniformly.
      const rawList = Array.isArray(response?.data) ? response.data : response?.data?.data || []
      optionsArray = rawList.map((option) => {
        return {
          id: option?.id,
          name: option?.name || option?.firstName,
          profilePicId: option?.profilePicId,
        }
      })
    }
    if (response?.status !== 200 || !optionsArray?.length) {
      hasMore = false
    }
    // Clinic-scoped responses are short, unpaginated lists — collapse pagination so
    // react-select-async-paginate doesn't spin trying to fetch a page 2 that the
    // backend would just respond to with another full list.
    if (clinicLocationId) {
      hasMore = false
    }

    return {
      options: optionsArray,
      hasMore,
      additional: {
        page: page + 1,
      },
    }
  }

  const handleMultiSelectPanelMembersChange = (value) => {
    setBasicEventObject((prev) => ({
      ...prev,
      panelIds: value.map((val) => val.id),
    }))
    setSelectedPanelMembers(value)
  }

  const formSections = (
    <>
      {layout === 'modal' && (
        <EventFormHeader
          eventFormModalData={eventFormModalData}
          onClose={() => setEventFormModalOpen(false)}
          isDisabled={isDisabled}
        />
      )}

      <EventBasicFields
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        isDisabled={isDisabled}
      />

      <EventTypeConfiguration
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        displayNamesOfEventTypes={displayNamesOfEventTypes}
        mentorProfileData={mentorProfileData}
        forceShowDoctorEventTypes={forceShowDoctorEventTypes}
        forceShowTherapistEventTypes={forceShowTherapistEventTypes}
        changeLocationBasedOnMentorOrClinicLocation={changeLocationBasedOnMentorOrClinicLocation}
        isDisabled={isDisabled}
      />

      <PanelAndWebinarConfig
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        selectedPanelMembers={selectedPanelMembers}
        handleMultiSelectPanelMembersChange={handleMultiSelectPanelMembersChange}
        loadOptions={loadOptions}
        isDisabled={isDisabled}
      />

      <DurationAndBuffers
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        isCustomTimeChosen={isCustomTimeChosen}
        setIsCustomTimeChosen={setIsCustomTimeChosen}
        isDisabled={isDisabled}
      />

      <SessionOptionsCheckboxes
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        oneOnOnePlans={oneOnOnePlans}
        showNextVisitSlotSelectorEnabler={showNextVisitSlotSelectorEnabler}
        isDisabled={isDisabled}
      />

      <PricingConfiguration
        basicEventObject={basicEventObject}
        viewPriceCountry={viewPriceCountry}
        setViewPriceCountry={setViewPriceCountry}
        oneOnOnePlans={oneOnOnePlans}
        bundlePacks={bundlePacks}
        unSecureSetupData={unSecureSetupData}
        changePlan={changePlan}
        handleEditOneOnOnePlan={handleEditOneOnOnePlan}
        setBundle={setBundle}
        refreshBundlePacks={refreshBundlePacks}
        mentorProfileData={mentorProfileData}
        isDisabled={isDisabled}
      />

      <LocationConfiguration
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        handleChangeLocation={handleChangeLocation}
        changeCountry={changeCountry}
        unSecureSetupData={unSecureSetupData}
        isDisabled={isDisabled}
      />

      <ClinicAssociationSelector
        basicEventObject={basicEventObject}
        setBasicEventObject={setBasicEventObject}
        mentorClinicAssociationData={mentorClinicAssociationData}
        changeLocationBasedOnMentorOrClinicLocation={changeLocationBasedOnMentorOrClinicLocation}
        isDisabled={isDisabled}
      />
    </>
  )

  const actionBar =
    !isDisabled && (
      <FormActionButtons
        onCancel={() => setEventFormModalOpen(false)}
        onSave={saveEvent}
        saving={saving}
        isValid={handleValidation(basicEventObject)}
        isEdit={Boolean(eventFormModalData) || !!eventFormModalData?.id}
        isDisabled={isDisabled}
        fullWidthActions={layout === 'drawer'}
      />
    )

  return (
    <>
      {layout === 'drawer' ? (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: { xs: 2, sm: 2.5 },
              py: 2,
            }}
          >
            <GenericDisplayFlexColumn style={{ gap: '22px', maxWidth: '100%' }}>
              {formSections}
            </GenericDisplayFlexColumn>
          </Box>
          {actionBar && (
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2, sm: 2.5 },
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: (theme) => theme.palette.grey[50],
              }}
            >
              {actionBar}
            </Box>
          )}
        </>
      ) : (
        <GenericDisplayFlexColumn style={{ gap: '24px', maxWidth: '420px' }}>
          {formSections}
          {actionBar}
        </GenericDisplayFlexColumn>
      )}

      <Drawer
        anchor="right"
        open={showAddCustomPrice}
        onClose={handleCloseAddCustomPrice}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 'min(100vw, 520px)', md: 560 },
            maxWidth: '100vw',
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      >
        <BaseAddCustomPriceComponent
          selectedPlan={oneOnOnePlanToEdit}
          handleChangePlan={changePlan}
          closeFunction={handleCloseAddCustomPrice}
          refreshOneOnOnePlans={refreshOneOnOnePlans}
          saveOneOnOnePlanApi={saveOneOnOnePlanApi}
          mentorProfileData={mentorProfileData}
        />
      </Drawer>
    </>
  )
}
